import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { Alert, Avatar, Button, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import firebase from 'firebase/compat/app';
import { ingestionStore } from './store/IngestionStore';
import { ingestionApi } from './ingestionApi';
import { useCurrentUser } from '../../user/permissions';
import { ITEM_TYPES, MODEL_ITEM_TYPES, PIPELINE_STEPS, SORT_OPTIONS, STATUS_GROUP_OPTIONS, STATUS_LABELS } from './itemTypes';

const { Title } = Typography;

const stepTitle = (step) => PIPELINE_STEPS.find((s) => s.step === step)?.title ?? step;

// Per admin, so two people sharing a browser keep their own view.
const filtersKey = (uid) => `ingestion.listFilters.${uid || 'anon'}`;

const loadFilters = (uid, defaults) => {
    try {
        const saved = JSON.parse(window.localStorage.getItem(filtersKey(uid)));
        if (!saved || typeof saved !== 'object') return defaults;
        return Object.fromEntries(Object.keys(defaults).map((key) => [key, saved[key] ?? defaults[key]]));
    } catch {
        return defaults;
    }
};

const saveFilters = (uid, filters) => {
    try {
        window.localStorage.setItem(filtersKey(uid), JSON.stringify(filters));
    } catch {
        /* storage blocked or full */
    }
};

export const IngestionPipeline = observer(() => {
    const history = useHistory();
    const me = firebase.auth().currentUser;
    const currentUserStore = useCurrentUser();
    const seesAllCreators = currentUserStore.can('upload-pipeline.read', 'all');

    // Default: my items that need manual work, newest created first.
    const [filters, setFilters] = useState(() =>
        loadFilters(me?.uid, {
            createdBy: me?.uid || '',
            step: '',
            statusGroup: 'manual',
            itemType: '',
            sortBy: 'createdAt',
        }),
    );
    const [creators, setCreators] = useState([]);

    useEffect(() => saveFilters(me?.uid, filters), [me?.uid, filters]);

    useEffect(() => {
        if (seesAllCreators) ingestionApi.getCreators().then(setCreators).catch(() => setCreators([]));
    }, [seesAllCreators]);

    // End-user submissions have no admin creator; they're a shared queue, so
    // the default "my items" view ORs them in. "App users" lists only them.
    const listParams = useMemo(
        () => ({ ...filters, includeUserSubmissions: filters.createdBy && filters.createdBy === me?.uid ? 'true' : '' }),
        [filters, me],
    );

    useEffect(() => {
        ingestionStore.loadList(listParams);
    }, [listParams]);

    const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

    const deleteItem = async (id) => {
        try {
            await ingestionApi.deleteItem(id);
            message.success('Item deleted');
            ingestionStore.loadList(listParams);
        } catch (e) {
            message.error(e.message || 'Failed to delete item');
        }
    };

    const creatorOptions = useMemo(() => {
        const known = creators.map((c) => ({
            value: c.uid,
            label: c.uid === me?.uid ? `${c.email || me?.email} (me)` : c.email || c.uid,
        }));
        // Ensure "me" is always selectable even before I've created any item.
        if (me?.uid && !creators.some((c) => c.uid === me.uid)) {
            known.unshift({ value: me.uid, label: `${me.email} (me)` });
        }
        return [{ value: '', label: 'All creators' }, { value: 'app-users', label: 'User requests' }, ...known];
    }, [creators, me]);

    const stepOptions = [
        { value: '', label: 'All steps' },
        ...PIPELINE_STEPS.map((s) => ({ value: s.step, label: `${s.step} · ${s.title}` })),
    ];

    const columns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        {
            title: 'Type',
            dataIndex: 'itemType',
            key: 'itemType',
            render: (type, record) => (
                <Space size={6}>
                    {type}
                    {record.modelEngine === 'opus' && MODEL_ITEM_TYPES.includes(type) && (
                        <Tooltip title="3D model by the Opus builder">
                            <Tag color="geekblue" style={{ marginRight: 0 }}>Opus</Tag>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: 'Created by',
            key: 'createdBy',
            align: 'center',
            render: (_, record) =>
                record.submittedByUid ? (
                    <Tooltip title={`Requested from the app by ${record.submittedByEmail || record.submittedByUid}`}>
                        <Space size={6}>
                            <Avatar size="small" style={{ backgroundColor: '#722ed1' }}>
                                {(record.submittedByEmail || 'U').charAt(0).toUpperCase()}
                            </Avatar>
                            <Tag color="purple" style={{ marginRight: 0 }}>User request</Tag>
                        </Space>
                    </Tooltip>
                ) : record.createdByUid ? (
                    <Tooltip title={record.createdByEmail || record.createdByUid}>
                        <Avatar size="small" src={record.createdByPhotoUrl || undefined}>
                            {(record.createdByEmail || '?').charAt(0).toUpperCase()}
                        </Avatar>
                    </Tooltip>
                ) : (
                    '—'
                ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const meta = STATUS_LABELS[status] || { text: status, color: 'default' };
                return <Tag color={meta.color}>{meta.text}</Tag>;
            },
        },
        {
            title: 'Step',
            dataIndex: 'currentStep',
            key: 'currentStep',
            render: (step) => `${step} · ${stepTitle(step)}`,
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (value) => (value ? new Date(value).toLocaleString() : '—'),
        },
        {
            title: '',
            key: 'actions',
            align: 'right',
            // Stop row-click navigation so interacting with the delete control (and
            // its confirm popover) doesn't open the item.
            onCell: () => ({ onClick: (e) => e.stopPropagation() }),
            render: (_, record) => currentUserStore.canOnItem('upload-pipeline.delete', record) && (
                <Popconfirm
                    title="Delete this item?"
                    description="This permanently removes the item and all its data."
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => deleteItem(record.id)}
                >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Title level={3} style={{ margin: 0 }}>
                    Upload pipeline
                </Title>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => ingestionStore.loadList(listParams)}
                        loading={ingestionStore.loadingList}
                    >
                        Refresh
                    </Button>
                    {currentUserStore.can('upload-pipeline.write') && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => history.push('/ingestion/new')}
                        >
                            New item
                        </Button>
                    )}
                </Space>
            </Space>

            {ingestionStore.listError && (
                <Alert type="error" message={ingestionStore.listError} style={{ marginBottom: 16 }} showIcon />
            )}

            <Space wrap style={{ marginBottom: 16 }}>
                {seesAllCreators && (
                    <Select
                        style={{ width: 240 }}
                        value={filters.createdBy}
                        onChange={(v) => setFilter('createdBy', v)}
                        options={creatorOptions}
                    />
                )}
                <Select
                    style={{ width: 160 }}
                    value={filters.step}
                    onChange={(v) => setFilter('step', v)}
                    options={stepOptions}
                />
                <Select
                    style={{ width: 200 }}
                    value={filters.statusGroup}
                    onChange={(v) => setFilter('statusGroup', v)}
                    options={STATUS_GROUP_OPTIONS}
                />
                <Select
                    style={{ width: 160 }}
                    value={filters.itemType}
                    onChange={(v) => setFilter('itemType', v)}
                    options={[{ value: '', label: 'All types' }, ...ITEM_TYPES.map((t) => ({ value: t, label: t }))]}
                />
                <Select
                    style={{ width: 180 }}
                    value={filters.sortBy}
                    onChange={(v) => setFilter('sortBy', v)}
                    options={SORT_OPTIONS}
                />
            </Space>

            <Table
                rowKey="id"
                loading={ingestionStore.loadingList}
                columns={columns}
                dataSource={ingestionStore.items.slice()}
                onRow={(record) => ({
                    onClick: () => history.push(`/ingestion/${record.id}`),
                    // User requests are a shared queue — tint them so they stand out from admin
                    // uploads. Translucent purple over the dark theme keeps the white text readable.
                    style: {
                        cursor: 'pointer',
                        ...(record.submittedByUid
                            ? { backgroundColor: 'rgba(146, 84, 222, 0.22)', boxShadow: 'inset 3px 0 0 #9254de' }
                            : {}),
                    },
                })}
            />
        </div>
    );
});
